import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import TankIcon from "@/components/TankIcon";
import { MessageCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import heroBackground from "@/assets/hero-background.jpg";

const Products = () => {
  const navigate = useNavigate();
  
  const heatedTanks = [
    {
      name: "Steam Jacketed Tanks",
      description: "High-efficiency heating for precise temperature control",
      specs: "50L - 50,000L capacity",
      material: "SS316L, Duplex",
      applications: "Pharmaceuticals, Food Processing"
    },
    {
      name: "Reactor Vessels",
      description: "Chemical reaction vessels with heating systems",
      specs: "100L - 15,000L capacity", 
      material: "SS316L, Hastelloy",
      applications: "Chemical, Biotech"
    },
    {
      name: "Hot Water Tanks",
      description: "Insulated hot water storage systems",
      specs: "500L - 150,000L capacity",
      material: "SS304, SS316L",
      applications: "Industrial, Commercial"
    },
    {
      name: "Heated Pressure Vessels",
      description: "High-pressure heated containment systems",
      specs: "Up to 16 bar pressure",
      material: "SS316L, Duplex",
      applications: "Chemical Processing"
    }
  ];

  const generalTanks = [
    {
      name: "Storage Tanks",
      description: "Large capacity storage for various liquids",
      specs: "1,000L - 150,000L capacity",
      material: "SS304, SS316L, Carbon Steel",
      applications: "Food, Chemical, Water Treatment"
    },
    {
      name: "Buffer Vessels",
      description: "Process buffer and holding tanks",
      specs: "100L - 20,000L capacity",
      material: "SS304, SS316L",
      applications: "Pharmaceuticals, Food & Beverage"
    },
    {
      name: "Water Tanks",
      description: "Potable and process water storage",
      specs: "500L - 150,000L capacity",
      material: "SS304, SS316L",
      applications: "Municipal, Industrial"
    },
    {
      name: "Mixing Tanks",
      description: "OEM mixing and blending vessels",
      specs: "50L - 60,000L capacity",
      material: "SS304, SS316L",
      applications: "Cosmetics, Pharmaceuticals"
    }
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Products | Stainless Steel Tanks & Vessels - WLE Technology</title>
        <meta name="description" content="Custom stainless steel tanks and vessels including heated tanks, reactor vessels, storage tanks, and mixing tanks for pharmaceutical, F&B, and chemical industries." />
        <link rel="canonical" href="https://wletec.com/products" />
        <meta property="og:title" content="WLE Technology Products - Tanks & Vessels" />
        <meta property="og:description" content="Custom-engineered tanks and vessels for every industry with premium finishing." />
        <meta property="og:url" content="https://wletec.com/products" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="stainless steel tanks, heated tanks, reactor vessels, storage tanks, mixing tanks, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="py-24 relative"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/75 via-slate-900/70 to-slate-900/65" />
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-foreground mb-6">
              <span className="text-wle-red">Our</span> <span className="text-white">Products</span>
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Custom-engineered tanks and vessels for every industry. From heated reactors to general storage, 
              all built to the highest standards with premium finishing.
            </p>
          </div>
        </div>
      </section>

      {/* Heated Tanks Section */}
      <section className="py-16">
        <div className="container">
          <div className="flex items-center gap-4 mb-12">
            <TankIcon type="heated" size="lg" />
            <div>
              <h2 className="text-3xl font-bold text-foreground">Heated Tanks & Vessels</h2>
              <p className="text-muted-foreground">Temperature-controlled systems for precise processing</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {heatedTanks.map((tank, index) => (
              <Card key={index} className="group hover:shadow-red transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <TankIcon type="heated" />
                    <Badge variant="destructive">Heated</Badge>
                  </div>
                  <CardTitle className="text-lg">{tank.name}</CardTitle>
                  <CardDescription>{tank.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium">Capacity</p>
                      <p className="text-sm text-muted-foreground">{tank.specs}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Materials</p>
                      <p className="text-sm text-muted-foreground">{tank.material}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Applications</p>
                      <p className="text-sm text-muted-foreground">{tank.applications}</p>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate('/contact')}>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Request a Quote Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* General Tanks Section */}
      <section className="py-16">
        <div className="container">
          <div className="flex items-center gap-4 mb-12">
            <TankIcon type="general" size="lg" />
            <div>
              <h2 className="text-3xl font-bold text-foreground">General Storage Tanks</h2>
              <p className="text-muted-foreground">Versatile storage solutions for all industries</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {generalTanks.map((tank, index) => (
              <Card key={index} className="group hover:shadow-blue transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <TankIcon type="general" />
                    <Badge className="bg-wle-blue text-wle-blue-foreground">General</Badge>
                  </div>
                  <CardTitle className="text-lg">{tank.name}</CardTitle>
                  <CardDescription>{tank.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium">Capacity</p>
                      <p className="text-sm text-muted-foreground">{tank.specs}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Materials</p>
                      <p className="text-sm text-muted-foreground">{tank.material}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Applications</p>
                      <p className="text-sm text-muted-foreground">{tank.applications}</p>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate('/contact')}>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Request a Quote Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container">
          <Card className="bg-gradient-primary text-white">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">Need a Custom Solution?</h3>
              <p className="text-lg mb-8 opacity-90">
                We design and build tanks to your exact specifications. From concept to completion.
              </p>
              <Button size="lg" variant="secondary" onClick={() => navigate('/contact')}>
                Get Custom Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Products;